import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const LAST_SEEN_KEY = "gameday-control-room-last-seen";
const ALERT_MUTES_KEY = "gameday-control-room-alert-mutes";

export const alertTypes = [
  { id: "investor-access", label: "Investor access requests" },
  { id: "email-failure", label: "Email delivery failures" },
];

function getAlertType(item) {
  if (item.activity_type === "investor_access_requested") return "investor-access";
  if (item.status === "error") return "email-failure";
  return "";
}

function readMutedAlerts() {
  try {
    const value = window.localStorage.getItem(ALERT_MUTES_KEY);
    return value ? JSON.parse(value) : {};
  } catch {
    return {};
  }
}

function savePreference(key, value) {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch {
    // Preferences are optional and should never interrupt Control Room activity.
    return false;
  }
}

function isPriorityEvent(item) {
  return Boolean(getAlertType(item));
}

function focusActivityEvent(activityId) {
  window.focus();
  window.document.getElementById(`activity-event-${activityId}`)?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
}

function notifyNewPriorityEvents(
  priorityEvents,
  mutedAlerts,
  notificationPermission,
  previousEventIds,
) {
  const newEvents = priorityEvents.filter((item) => !previousEventIds.has(item.activity_id));
  const isInitialSnapshot = previousEventIds.size === 0;
  previousEventIds.clear();
  priorityEvents.forEach((item) => previousEventIds.add(item.activity_id));
  const unmutedEvents = newEvents.filter((item) => !mutedAlerts[getAlertType(item)]);
  if (notificationPermission !== "granted" || isInitialSnapshot || !unmutedEvents.length) return;
  const primaryEvent = unmutedEvents[0];
  const browserAlert = new window.Notification("GAMEDAY Control Room", {
    body:
      `${unmutedEvents.length} new priority alert`
      + `${unmutedEvents.length === 1 ? "" : "s"} requires attention.`,
  });
  browserAlert.onclick = () => {
    browserAlert.close();
    focusActivityEvent(primaryEvent.activity_id);
  };
}

export default function usePriorityActivityAlerts(items) {
  const [lastSeenAt, setLastSeenAt] = useState(() => {
    return window.localStorage.getItem(LAST_SEEN_KEY) || "";
  });
  const [notificationPermission, setNotificationPermission] = useState(() => {
    return "Notification" in window ? window.Notification.permission : "unsupported";
  });
  const [mutedAlerts, setMutedAlerts] = useState(readMutedAlerts);
  const previousEventIds = useRef(new Set());
  const priorityEvents = useMemo(() => items.filter(isPriorityEvent), [items]);
  const activePriorityEvents = useMemo(
    () => priorityEvents.filter((item) => !mutedAlerts[getAlertType(item)]),
    [mutedAlerts, priorityEvents],
  );
  const newPriorityCount = useMemo(
    () => {
      return activePriorityEvents.filter((item) => {
        return !lastSeenAt || new Date(item.created_at).getTime() > new Date(lastSeenAt).getTime();
      }).length;
    },
    [activePriorityEvents, lastSeenAt],
  );

  useEffect(() => {
    notifyNewPriorityEvents(
      priorityEvents,
      mutedAlerts,
      notificationPermission,
      previousEventIds.current,
    );
  }, [mutedAlerts, notificationPermission, priorityEvents]);

  const markAllRead = useCallback(() => {
    const timestamp = new Date().toISOString();
    savePreference(LAST_SEEN_KEY, timestamp);
    setLastSeenAt(timestamp);
  }, []);

  const enableBrowserAlerts = useCallback(async () => {
    if (!("Notification" in window)) return;
    setNotificationPermission(await window.Notification.requestPermission());
  }, []);

  const toggleAlertMute = useCallback((alertType) => {
    setMutedAlerts((currentMutes) => {
      const nextMutes = {
        ...currentMutes,
        [alertType]: !currentMutes[alertType],
      };
      savePreference(ALERT_MUTES_KEY, JSON.stringify(nextMutes));
      return nextMutes;
    });
  }, []);

  return {
    enableBrowserAlerts,
    markAllRead,
    newPriorityCount,
    notificationPermission,
    mutedAlerts,
    toggleAlertMute,
  };
}